import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  Download,
  MessageSquare,
  Calendar,
  RefreshCcw,
  Award,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";
import { api, reportUrl } from "@/lib/api";
import { PILLARS } from "@/data/pillars";
import { classify, analyze, actionsFor } from "@/lib/scoring";
import { useDiagnostic } from "@/context/DiagnosticContext";

export default function Results() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { reset } = useDiagnostic();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get(`/diagnostics/${id}`);
        if (mounted) setData(res.data);
      } catch (e) {
        if (mounted) setErr("Diagnóstico não encontrado.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  const analysis = useMemo(
    () => (data ? analyze(data.pillar_scores) : null),
    [data]
  );

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-slate-500">
        Carregando diagnóstico…
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-slate-600 px-6">
        <div className="text-center">
          <div className="text-[#DC2626]">{err || "Erro desconhecido."}</div>
          <button
            className="hub-btn-primary mt-6"
            onClick={() => navigate("/")}
          >
            Voltar ao início
          </button>
        </div>
      </div>
    );
  }

  const radarData = PILLARS.map((p) => ({
    pillar: p.name.split(" ").slice(0, 2).join(" "),
    fullName: p.name,
    score: data.pillar_scores[p.id] || 0,
  }));

  const barData = PILLARS.map((p) => ({
    name: p.name,
    score: data.pillar_scores[p.id] || 0,
    fill: classify(data.pillar_scores[p.id] || 0).color,
  })).sort((a, b) => b.score - a.score);

  const totalClass = classify(data.total_score);

  return (
    <div data-testid="results-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
      {/* HERO */}
      <div className="grid lg:grid-cols-12 gap-8 items-stretch">
        <div className="lg:col-span-7 hub-card p-8 lg:p-10">
          <div className="flex items-center justify-between">
            <div className="hub-eyebrow">Índice HUB · Desenvolvimento Empresarial</div>
            <div className="text-xs text-slate-500">
              {new Date(data.created_at).toLocaleDateString("pt-BR")}
            </div>
          </div>

          <div className="mt-4 flex items-end gap-4">
            <div
              data-testid="total-score"
              className="font-display font-medium text-7xl sm:text-8xl tracking-tighter leading-none text-[#0F172A]"
            >
              {Math.round(data.total_score)}
            </div>
            <div className="pb-3 text-slate-400 text-2xl">/100</div>
          </div>

          <div
            data-testid="badge-label"
            className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0F172A] text-white text-sm"
          >
            <Award size={14} className="text-[#D97706]" />
            {data.badge}
          </div>

          <p className="mt-6 text-slate-700 leading-relaxed max-w-xl">
            A <strong>{data.company.empresa}</strong> está à frente de aproximadamente{" "}
            <strong data-testid="benchmark-value" className="text-[#D97706]">
              {data.benchmark_percent}%
            </strong>{" "}
            das empresas avaliadas na maturidade em desenvolvimento empresarial.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              data-testid="download-pdf-btn"
              href={reportUrl(data.id)}
              className="hub-btn-primary"
              target="_blank"
              rel="noreferrer"
            >
              <Download size={16} /> Baixar Relatório em PDF
            </a>
            <button
              data-testid="btn-restart"
              onClick={() => {
                reset();
                navigate("/");
              }}
              className="hub-btn-secondary"
            >
              <RefreshCcw size={16} /> Novo diagnóstico
            </button>
          </div>
        </div>

        {/* Radar */}
        <div className="lg:col-span-5 hub-card p-8">
          <div className="hub-eyebrow">Perfil por pilar</div>
          <h3 className="mt-2 font-display font-medium text-lg tracking-tight">
            Radar de maturidade
          </h3>
          <div className="mt-4 h-[320px]" data-testid="radar-chart">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} outerRadius="75%">
                <PolarGrid stroke="#E2E8F0" />
                <PolarAngleAxis
                  dataKey="pillar"
                  tick={{ fill: "#475569", fontSize: 11 }}
                />
                <PolarRadiusAxis
                  angle={90}
                  domain={[0, 100]}
                  tick={{ fill: "#94A3B8", fontSize: 10 }}
                />
                <Radar
                  dataKey="score"
                  stroke="#0F172A"
                  fill="#0F172A"
                  fillOpacity={0.15}
                  strokeWidth={2}
                />
                <Tooltip
                  formatter={(v) => [`${Math.round(v)}%`, "Score"]}
                  labelFormatter={(l) =>
                    radarData.find((r) => r.pillar === l)?.fullName || l
                  }
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* BARS + ANALYSIS */}
      <div className="mt-8 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 hub-card p-8">
          <div className="hub-eyebrow">Desempenho por pilar</div>
          <h3 className="mt-2 font-display font-medium text-lg tracking-tight">
            Ranking de pontuação
          </h3>
          <div className="mt-6 h-[360px]" data-testid="bar-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={barData}
                layout="vertical"
                margin={{ left: 20, right: 30, top: 6, bottom: 6 }}
              >
                <XAxis
                  type="number"
                  domain={[0, 100]}
                  tick={{ fill: "#94A3B8", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  dataKey="name"
                  type="category"
                  tick={{ fill: "#0F172A", fontSize: 12 }}
                  width={170}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  formatter={(v) => [`${Math.round(v)}%`, "Score"]}
                  cursor={{ fill: "#F1F5F9" }}
                />
                <Bar dataKey="score" radius={[0, 8, 8, 0]}>
                  {barData.map((entry, index) => (
                    <Cell key={index} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-6 flex flex-wrap gap-4 text-xs text-slate-600">
            <Legend color="#16A34A" label="Excelente (≥80)" />
            <Legend color="#65A30D" label="Bom (60–79)" />
            <Legend color="#FACC15" label="Atenção (40–59)" />
            <Legend color="#DC2626" label="Crítico (<40)" />
          </div>
        </div>

        {/* Executive summary */}
        <div className="lg:col-span-5 hub-card p-8">
          <div className="hub-eyebrow">Resumo executivo</div>
          <h3 className="mt-2 font-display font-medium text-lg tracking-tight">
            Análise automática
          </h3>
          <div className="mt-6 space-y-5" data-testid="executive-summary">
            <SummaryLine
              icon={<Sparkles size={16} className="text-[#16A34A]" />}
              label="Principal ponto forte"
              value={analysis.strongest.name}
              score={analysis.strongest.score}
            />
            <SummaryLine
              icon={<TrendingUp size={16} className="text-[#DC2626]" />}
              label="Maior gargalo atual"
              value={analysis.weakest.name}
              score={analysis.weakest.score}
            />
            <div>
              <div className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-500">
                Prioridades para desenvolvimento
              </div>
              <ol className="mt-3 space-y-2">
                {analysis.priorities.map((p, i) => (
                  <li
                    key={p.id}
                    className="flex items-center justify-between gap-2 text-sm"
                  >
                    <span className="flex items-center gap-2 text-[#0F172A]">
                      <span className="w-6 h-6 rounded-full bg-[#0F172A] text-white grid place-items-center text-[11px] font-medium">
                        {i + 1}
                      </span>
                      {p.name}
                    </span>
                    <span
                      className="font-mono text-xs"
                      style={{ color: classify(p.score).color }}
                    >
                      {Math.round(p.score)}%
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="mt-8 p-4 bg-[#F8FAFC] rounded-xl border border-slate-200 text-sm text-slate-700">
            Classificação global:{" "}
            <span className="font-medium" style={{ color: totalClass.color }}>
              {totalClass.label}
            </span>
          </div>
        </div>
      </div>

      {/* ACTION PLAN */}
      <div className="mt-12">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="hub-eyebrow">Plano de Ação</div>
            <h3 className="mt-2 font-display font-medium text-2xl sm:text-3xl tracking-tight">
              Três prioridades para acelerar sua evolução
            </h3>
          </div>
          <a
            href={reportUrl(data.id)}
            data-testid="download-pdf-btn-2"
            className="hub-btn-secondary"
            target="_blank"
            rel="noreferrer"
          >
            <Download size={16} /> Exportar em PDF
          </a>
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {analysis.priorities.map((p, i) => {
            const c = classify(p.score);
            return (
              <div
                key={p.id}
                className="hub-card p-7"
                data-testid={`priority-card-${i + 1}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="hub-eyebrow">Prioridade {i + 1}</div>
                    <div className="mt-2 font-display font-medium text-xl tracking-tight">
                      {p.name}
                    </div>
                  </div>
                  <div
                    className="text-xs font-mono px-2 py-1 rounded-full border"
                    style={{ color: c.color, borderColor: c.color + "33" }}
                  >
                    {Math.round(p.score)}%
                  </div>
                </div>
                <ul className="mt-5 space-y-2">
                  {actionsFor(p.id).map((a, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed"
                    >
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#D97706] flex-shrink-0" />
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {/* CTA */}
      <div className="mt-16 hub-card overflow-hidden">
        <div className="grid lg:grid-cols-5">
          <div className="lg:col-span-3 p-10 bg-[#0F172A] text-white">
            <div className="text-xs uppercase tracking-[0.22em] text-[#D97706] font-semibold">
              Próximo passo
            </div>
            <h3 className="mt-3 font-display font-medium text-3xl tracking-tight leading-tight">
              Gostaria de acelerar esses resultados com um especialista HUB?
            </h3>
            <p className="mt-4 text-slate-300 leading-relaxed max-w-md">
              Em uma sessão estratégica, você recebe um plano tático personalizado
              para transformar este diagnóstico em resultado prático nos próximos
              90 dias.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="https://wa.me/5571984842497"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-speak-specialist"
                className="hub-btn-primary bg-white text-[#0F172A] border-white hover:bg-slate-100"
              >
                <MessageSquare size={16} /> Quero conversar com um especialista HUB
              </a>
              <a
                href="https://calendar.app.google/5j6oU8BYKsT3Z5qXA"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-schedule-session"
                className="hub-btn-secondary bg-transparent text-white border-white/30 hover:bg-white/10"
              >
                <Calendar size={16} /> Agendar Sessão Estratégica
              </a>
            </div>
          </div>
          <div className="lg:col-span-2 p-10 bg-white">
            <div className="hub-eyebrow">O que você recebe</div>
            <ul className="mt-4 space-y-3 text-sm text-slate-700">
              <BulletCTA text="Análise aprofundada dos 3 pilares críticos" />
              <BulletCTA text="Roteiro tático de 90 dias com marcos claros" />
              <BulletCTA text="Recomendações de rituais de liderança e gestão" />
              <BulletCTA text="Apoio na priorização de investimentos em pessoas" />
            </ul>
            <div className="mt-6 text-xs text-slate-500">
              Todos os dados do seu diagnóstico são tratados de forma confidencial.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryLine({ icon, label, value, score }) {
  const c = classify(score);
  return (
    <div>
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-semibold text-slate-500">
        {icon} {label}
      </div>
      <div className="mt-1 flex items-baseline justify-between gap-2">
        <div className="font-display font-medium text-lg tracking-tight text-[#0F172A]">
          {value}
        </div>
        <div className="font-mono text-sm" style={{ color: c.color }}>
          {Math.round(score)}%
        </div>
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="w-3 h-3 rounded-sm"
        style={{ backgroundColor: color }}
      />
      {label}
    </div>
  );
}

function BulletCTA({ text }) {
  return (
    <li className="flex items-start gap-2">
      <ArrowRight size={14} className="mt-1 text-[#D97706]" />
      <span>{text}</span>
    </li>
  );
}
